<template>
  <main class="home-node-page">
    <header class="node-header">
      <!-- <span /> -->
      <button type="button" class="node-header__close" @click="goBack">
        <van-icon name="arrow-left" size="28" style="color: #000" />
      </button>
      <h1>{{ t("home.selectNode") }}</h1>
    </header>

    <van-loading v-if="loading" class="node-loading" />

    <section v-else class="node-list">
      <button
        v-for="item in nodes"
        :key="nodeKey(item)"
        type="button"
        class="node-row"
        :class="{ active: isSelected(item) }"
        @click="selectNode(item)"
      >
        <div class="node-row__main">
          <div class="node-row__name">
            <div data-strong>
              {{ item.name || item.chainName || t("home.node") }}
            </div>
            <van-icon v-if="isSelected(item)" name="success" />
          </div>
          <div class="node-row__delay">
            <div data-strong>{{ item.delayTime ?? "--" }}</div>
            <span>ms</span>
            <i :class="['node-row__dot', dotClass(item.status)]" />
          </div>
        </div>

        <div class="node-row__sub">
          <span>{{ item.rpcUrl || "--" }}</span>
          <em>{{ t("home.blockHeight") }} {{ item.height ?? 0 }}</em>
        </div>
      </button>
    </section>
  </main>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { chainNodeList } from "@/api/wallet";
import { useProfileStore } from "@/stores/profile";

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();

const loading = ref(false);
const nodes = ref([]);
const selectedNode = ref(null);

function nodeKey(node = {}) {
  return String(node?.id || node?.rpcUrl || node?.name || "").trim();
}

function isSelected(node) {
  return nodeKey(node) === nodeKey(selectedNode.value);
}

function dotClass(status = 1) {
  if (Number(status) <= 1) return "node-row__dot--ok";
  if (Number(status) === 2) return "node-row__dot--warn";
  return "node-row__dot--bad";
}

async function loadNodes() {
  loading.value = true;
  try {
    const result = await chainNodeList({
      chainNum: profileStore.currentNetwork.chainNum || 56,
    });
    nodes.value = Array.isArray(result)
      ? result
      : Array.isArray(result?.list)
        ? result.list
        : [];
    const current = profileStore.currentNetwork.currentNode || {};
    selectedNode.value =
      nodes.value.find(
        (item) =>
          current.id === item.id ||
          current.rpcUrl === item.rpcUrl ||
          current.name === item.name,
      ) ||
      current ||
      nodes.value[0] ||
      null;

    if (!profileStore.currentNetwork?.currentNode?.id && nodes.value.length) {
      await applyNode(nodes.value[0], false);
    }
  } catch (error) {
    nodes.value = [];
  } finally {
    loading.value = false;
  }
}

async function applyNode(node, leave = true) {
  const nextChainNum = node.chainNum || profileStore.currentNetwork.chainNum;
  const matchedNetwork = profileStore.networkList.find(
    (item) => String(item?.chainNum || "") === String(nextChainNum || ""),
  );
  const baseNetwork = matchedNetwork || profileStore.currentNetwork;

  await profileStore.setCurrentNetwork({
    ...baseNetwork,
    chainNum: nextChainNum,
    rpcUrl: node.rpcUrl || baseNetwork.rpcUrl,
    currentNode: node,
  });
  selectedNode.value = node;

  if (leave) {
    router.replace("/home/index");
  }
}

function selectNode(node) {
  applyNode(node);
}

function goBack() {
  router.replace("/home/index");
}

onMounted(loadNodes);
</script>

<style scoped lang="scss">
.home-node-page {
  min-height: var(--app-viewport-height);
  background: #ffffff;
}

.node-header {
  position: sticky;
  top: 0;
  z-index: 2;
  display: grid;
  grid-template-columns: 24px 1fr 48px;
  align-items: center;
  height: calc(60px + env(safe-area-inset-top));
  padding: env(safe-area-inset-top) 15px 0;
  background: #ffffff;

  h1 {
    margin: 0;
    color: #000000;
    font-size: 18px;
    margin-left: 24px;
    line-height: 25px;
    font-weight: 700;
    text-align: center;
  }
}

.node-header__close {
  justify-self: end;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;

  .app-image {
    width: 18px;
    height: 18px;
    display: block;
  }
}

.node-loading {
  display: flex;
  width: 100%;
  justify-content: center;
  margin: 80px auto 0;
}

.node-list {
  padding-top: 4px;
}

.node-row {
  width: 100%;
  min-height: 70px;
  padding: 12px 15px;
  border: 0;
  background: #ffffff;
  color: #1d2129;
  text-align: left;

  &.active {
    background: var(--wp-color-card-1);
  }
}

.node-row__main,
.node-row__sub {
  display: flex;
  align-items: center;
  gap: 10px;
}

.node-row__main {
  justify-content: space-between;
}

.node-row__name {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;

  [data-strong] {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #1d2129;
    font-size: 15px;
    line-height: 21px;
    font-weight: 500;
  }

  .van-icon {
    color: #00b42a;
    font-size: 18px;
    flex-shrink: 0;
  }
}

.node-row__delay {
  display: flex;
  align-items: center;
  gap: 3px;
  color: #1d2129;
  flex-shrink: 0;

  [data-strong] {
    font-size: 16px;
    line-height: 22px;
    font-weight: 700;
  }

  span {
    font-size: 14px;
    line-height: 20px;
  }
}

.node-row__dot {
  width: 10px;
  height: 10px;
  margin-left: 4px;
  border-radius: 50%;
  background: #00b42a;
}

.node-row__dot--warn {
  background: #fa8c15;
}

.node-row__dot--bad {
  background: #f53f3f;
}

.node-row__sub {
  margin-top: 5px;
  color: #999999;
  font-size: 12px;
  line-height: 17px;

  span {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  em {
    flex-shrink: 0;
    font-style: normal;
  }
}
</style>
